import { useCallback, useEffect, useRef, useState } from "react";
import { uploadAttachment } from "../api/client";
import type { Attachment } from "../api/types";
import { errorMessage } from "./format";

export interface UploadItem {
  key: string;
  file: File;
  /** object URL for image previews */
  preview: string | null;
  progress: number;
  attachment: Attachment | null;
  error: string | null;
}

let seq = 0;

/** Manages a list of files being uploaded via POST /api/attachments. */
export function useUploads() {
  const [items, setItems] = useState<UploadItem[]>([]);
  const controllers = useRef(new Map<string, AbortController>());
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(
    () => () => {
      controllers.current.forEach((c) => c.abort());
      itemsRef.current.forEach((i) => i.preview && URL.revokeObjectURL(i.preview));
    },
    [],
  );

  const update = (key: string, patch: Partial<UploadItem>) =>
    setItems((all) => all.map((i) => (i.key === key ? { ...i, ...patch } : i)));

  const addFiles = useCallback((files: FileList | File[]) => {
    const list = Array.from(files);
    for (const raw of list) {
      // Pasted images often arrive as "image.png"; give them a unique-ish name.
      const file =
        raw.name === "image.png" || !raw.name
          ? new File([raw], `pasted-${new Date().toISOString().replace(/[:.]/g, "-")}.${
              raw.type.split("/")[1] || "png"
            }`, { type: raw.type })
          : raw;
      const key = `u${++seq}`;
      const preview = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
      const ctrl = new AbortController();
      controllers.current.set(key, ctrl);
      setItems((all) => [...all, { key, file, preview, progress: 0, attachment: null, error: null }]);
      uploadAttachment(file, (p) => update(key, { progress: p }), ctrl.signal)
        .then((attachment) => update(key, { attachment, progress: 1 }))
        .catch((e) => {
          if (!ctrl.signal.aborted) update(key, { error: errorMessage(e) });
        })
        .finally(() => controllers.current.delete(key));
    }
  }, []);

  const remove = useCallback((key: string) => {
    controllers.current.get(key)?.abort();
    setItems((all) => {
      const it = all.find((i) => i.key === key);
      if (it?.preview) URL.revokeObjectURL(it.preview);
      return all.filter((i) => i.key !== key);
    });
  }, []);

  const clear = useCallback(() => {
    setItems((all) => {
      all.forEach((i) => i.preview && URL.revokeObjectURL(i.preview));
      return [];
    });
  }, []);

  const busy = items.some((i) => !i.attachment && !i.error);
  const ids = items.filter((i) => i.attachment).map((i) => i.attachment!.id);
  return { items, addFiles, remove, clear, busy, ids };
}

export type Uploads = ReturnType<typeof useUploads>;
